import { Component, h } from 'preact';
import { Subject, Subscription } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';

import { Form, Field, FieldList } from '../../../utils/forms/form';
import hopplerLinkState, { hopplerUpdateFormFieldValue } from '../../../utils/forms/hoppler-linkstate';

import { MoneyCalculator } from '../../../../core/money-calculator';
import { toDecimal } from '../../../../helpers/decimal';

import {
  DEFAULT_DOCUMENTARY_FEE_PERCENTAGE,
  DEFAULT_REGISTRATION_FEE_PERCENTAGE,
  DEFAULT_TRANSFER_FEE_PERCENTAGE,
  DEFAULT_NOTARIAL_FEE_PERCENTAGE,
  DEFAULT_ADMIN_FEE  
} from '../../../../constants/calculators';
import { CurrencyInput } from '../commons/hoppler-currency-input.component';

export interface TransferCostCalculatorProps {
  'listing-price'?: string;
}

interface TransferCostCalculatorState {
  listingPrice?: number;
  transferCostForm?: Form;
  totalAdditionalFees?: number;
  totalPrice?: number;
}

'hi-transfer-cost-calculator';
export class TransferCostCalculator extends Component<TransferCostCalculatorProps, TransferCostCalculatorState> {
  form$: Subject<Form>;
  moneyCalculator: MoneyCalculator;

  private subscriptions: Subscription[] = [];
  
  constructor(props: TransferCostCalculatorProps) {
    super(props);
    this.moneyCalculator = new MoneyCalculator();

    this.setState({
      listingPrice: parseInt(props['listing-price'])
    });

    this.setupForm();
    const next = this.calculateEstimates(this.state.transferCostForm);
    this.calculateAdditionalFees(next);
  }

  setupForm() {
    let fields: FieldList = {
      'documentaryStampTax': new Field(
        [],
         null, 
         this.moneyCalculator.convertPercentToValue(
          DEFAULT_DOCUMENTARY_FEE_PERCENTAGE,
          this.state.listingPrice
        )
      ),
      'documentaryStampTaxPercentage': new Field([], null, DEFAULT_DOCUMENTARY_FEE_PERCENTAGE),

      'registrationFee': new Field(
        [],
         null, 
         this.moneyCalculator.convertPercentToValue(
          DEFAULT_REGISTRATION_FEE_PERCENTAGE,
          this.state.listingPrice
        )
      ),
      'registrationFeePercentage': new Field([], null, DEFAULT_REGISTRATION_FEE_PERCENTAGE),

      'transferFee': new Field(
        [],
         null, 
         this.moneyCalculator.convertPercentToValue(
          DEFAULT_TRANSFER_FEE_PERCENTAGE,
          this.state.listingPrice
        )
      ),
      'transferFeePercentage': new Field([], null, DEFAULT_TRANSFER_FEE_PERCENTAGE),

      'notarialFee': new Field(
        [],
         null, 
         this.moneyCalculator.convertPercentToValue(
          DEFAULT_NOTARIAL_FEE_PERCENTAGE,
          this.state.listingPrice
        )
      ),
      'notarialFeePercentage': new Field([], null, DEFAULT_NOTARIAL_FEE_PERCENTAGE),

      'adminFee': new Field([], null, DEFAULT_ADMIN_FEE),
      'adminFeePercentage': new Field(
        [],
        null, 
        this.moneyCalculator.convertValueToPercent(
          DEFAULT_ADMIN_FEE, 
          this.state.listingPrice
          )
        ),
    };

    this.form$ = new Subject<Form>();

    this.setState({
      transferCostForm: new Form(fields)
    });

    this.subscriptions.push(
      this.form$.pipe(distinctUntilChanged())
        .subscribe(form => {
          const nextForm = this.calculateEstimates(form);

          this.calculateAdditionalFees(nextForm);

          let state = {};
          state['transferCostForm'] = nextForm;
          this.setState(state);
        })
    );
    
  }

  componentWillUnmount() {
    this.subscriptions.forEach(member => {
      member.unsubscribe();
    });
  }

  calculateEstimates(curr: Form): Form {
    let prev = this.state.transferCostForm

    let next = Object.assign(
      {},
      curr
    );

    // documentaryStampTax & documentaryStampTaxPercentage
    if (curr.fields['documentaryStampTaxPercentage'].value != prev.fields['documentaryStampTaxPercentage'].value) {
      next.fields['documentaryStampTax'].value = this.moneyCalculator.convertPercentToValue(
        curr.fields['documentaryStampTaxPercentage'].value,
        this.state.listingPrice
      );
    }

    if (curr.fields['documentaryStampTax'].value != prev.fields['documentaryStampTax'].value) {
      next.fields['documentaryStampTaxPercentage'].value = this.moneyCalculator.convertValueToPercent(
        curr.fields['documentaryStampTax'].value,
        this.state.listingPrice
      );
    }

    // registrationFee & registrationFeePercentage
    if (curr.fields['registrationFeePercentage'].value != prev.fields['registrationFeePercentage'].value) {
      next.fields['registrationFee'].value = this.moneyCalculator.convertPercentToValue(
        curr.fields['registrationFeePercentage'].value,
        this.state.listingPrice
      );
    }

    if (curr.fields['registrationFee'].value != prev.fields['registrationFee'].value) {
      next.fields['registrationFeePercentage'].value = this.moneyCalculator.convertValueToPercent(
        curr.fields['registrationFee'].value,
        this.state.listingPrice
      );
    }

    // transferFee & transferFeePercentage
    if (curr.fields['transferFeePercentage'].value != prev.fields['transferFeePercentage'].value) {
      next.fields['transferFee'].value = this.moneyCalculator.convertPercentToValue(
        curr.fields['transferFeePercentage'].value,
        this.state.listingPrice
      );
    }

    if (curr.fields['transferFee'].value != prev.fields['transferFee'].value) {
      next.fields['transferFeePercentage'].value = this.moneyCalculator.convertValueToPercent(
        curr.fields['transferFee'].value,
        this.state.listingPrice
      );
    }

    // notarialFee & notarialFeePercentage
    if (curr.fields['notarialFeePercentage'].value != prev.fields['notarialFeePercentage'].value) {
      next.fields['notarialFee'].value = this.moneyCalculator.convertPercentToValue(
        curr.fields['notarialFeePercentage'].value,
        this.state.listingPrice
      );
    }

    if (curr.fields['notarialFee'].value != prev.fields['notarialFee'].value) {
      next.fields['notarialFeePercentage'].value = this.moneyCalculator.convertValueToPercent(
        curr.fields['notarialFee'].value,
        this.state.listingPrice
      );
    }

    // adminFee & adminFeePercentage
    if (curr.fields['adminFeePercentage'].value != prev.fields['adminFeePercentage'].value) {
      next.fields['adminFee'].value = this.moneyCalculator.convertPercentToValue(
        curr.fields['adminFeePercentage'].value,
        this.state.listingPrice
      );
    }

    if (curr.fields['adminFee'].value != prev.fields['adminFee'].value) {
      next.fields['adminFeePercentage'].value = this.moneyCalculator.convertValueToPercent(
        curr.fields['adminFee'].value,
        this.state.listingPrice
      );
    }

    return next;
  }

  calculateAdditionalFees(form: Form) {
    let additionalFees: number = 0;

    additionalFees+= parseInt(form.fields['documentaryStampTax'].value);
    additionalFees+= parseInt(form.fields['registrationFee'].value);
    additionalFees+= parseInt(form.fields['transferFee'].value);
    additionalFees+= parseInt(form.fields['notarialFee'].value);
    additionalFees+= parseInt(form.fields['adminFee'].value);

    const newTotalPrice: number = this.state.listingPrice + additionalFees;
    this.setState({
      totalAdditionalFees: additionalFees,
      totalPrice: newTotalPrice
    });
  }

  onDocTaxChange = (value) => {
    let form = this.state.transferCostForm;
    hopplerUpdateFormFieldValue(this.form$, form, 'documentaryStampTax',value);
  }

  onRegFeeChange = (value) => {
    let form = this.state.transferCostForm;
    hopplerUpdateFormFieldValue(this.form$, form, 'registrationFee',value);
  }

  onTransferFeeChange = (value) => {
    let form = this.state.transferCostForm;
    hopplerUpdateFormFieldValue(this.form$, form, 'transferFee',value);
  }

  onNotFeeChange = (value) => {
    let form = this.state.transferCostForm;
    hopplerUpdateFormFieldValue(this.form$, form, 'notarialFee',value);
  }

  onAdminFeeChange = (value) => {
    let form = this.state.transferCostForm;
    hopplerUpdateFormFieldValue(this.form$, form, 'adminFee',value);
  }

  render(props: TransferCostCalculatorProps, state: TransferCostCalculatorState) {
    let form = state.transferCostForm;

    return (
      <div class="hi-transfer-cost-calculator my-3">
        <div class="p-md-4">
          <span class="hi-transfer-cost-calculator__heading">Transfer Cost Calculator</span>
          <p>Default values are estimates and actual values may vary.</p>

          <div class="row">
            <div class="col-12 col-md-6">
              <form>
                <div class="form-group">
                  <label>Documentary Stamp Tax</label>
                  <div class="hi-transfer-cost-calculator__money-row">
                    <div class="input-group hi-transfer-cost-calculator__money-php">
                      <div class="input-group-prepend">
                        <span class="input-group-text bg-white text-muted">Php</span>
                      </div>
                      {/* <input class="form-control border-left-0" 
                        type="number"
                        value={form.fields['documentaryStampTax'].value}
                        onChange={hopplerLinkState(this.form$, form, 'documentaryStampTax')}>
                      </input> */}

                      <CurrencyInput
                        extraClasses="border-left-0"
                        onValueChange={ this.onDocTaxChange }
                        value={ form.fields['documentaryStampTax'].value }
                        fieldName="documentaryStampTax"
                        placeholder={ form.fields['documentaryStampTax'].value }
                        min={ 0 }>
                      </CurrencyInput>
                    </div>

                    <div class="input-group hi-transfer-cost-calculator__money-pct">
                      <input class="form-control border-right-0 pr-0" 
                        type="number"
                        min={ 0 }
                        value={form.fields['documentaryStampTaxPercentage'].value}
                        onChange={hopplerLinkState(this.form$, form, 'documentaryStampTaxPercentage')}>
                      </input>
                      <div class="input-group-append">
                        <span class="input-group-text bg-white text-muted">%</span>
                      </div>
                    </div>
                  </div>

                </div>

                <div class="form-group">
                  <label>Registration Fee</label>
                  <div class="hi-transfer-cost-calculator__money-row">
                    <div class="input-group hi-transfer-cost-calculator__money-php">
                      <div class="input-group-prepend">
                        <span class="input-group-text bg-white text-muted">Php</span>
                      </div>
                      {/* <input class="form-control border-left-0" 
                        type="number"
                        value={form.fields['registrationFee'].value}
                        onChange={hopplerLinkState(this.form$, form, 'registrationFee')}>
                      </input> */}
                      <CurrencyInput
                        extraClasses="border-left-0"
                        onValueChange={ this.onRegFeeChange }
                        value={ form.fields['registrationFee'].value }
                        fieldName="registrationFee"
                        placeholder={ form.fields['registrationFee'].value }
                        min={ 0 }>
                      </CurrencyInput>
                    </div>

                    <div class="input-group hi-transfer-cost-calculator__money-pct">
                      <input class="form-control border-right-0 pr-0" 
                        type="number"
                        min={ 0 }
                        value={form.fields['registrationFeePercentage'].value}
                        onChange={hopplerLinkState(this.form$, form, 'registrationFeePercentage')}>
                      </input>
                      <div class="input-group-append">
                        <span class="input-group-text bg-white text-muted">%</span>
                      </div>
                    </div>
                  </div>

                </div>

                <div class="form-group">
                  <label>Transfer fee</label>
                  <div class="hi-transfer-cost-calculator__money-row">
                    <div class="input-group hi-transfer-cost-calculator__money-php">
                      <div class="input-group-prepend">
                        <span class="input-group-text bg-white text-muted">Php</span>
                      </div>
                      {/* <input class="form-control border-left-0" 
                        type="number"
                        value={form.fields['transferFee'].value}
                        onChange={hopplerLinkState(this.form$, form, 'transferFee')}>
                      </input> */}

                      <CurrencyInput
                        extraClasses="border-left-0"
                        onValueChange={ this.onTransferFeeChange }
                        value={ form.fields['transferFee'].value }
                        fieldName="transferFee"
                        placeholder={ form.fields['transferFee'].value }
                        min={ 0 }>
                      </CurrencyInput>
                    </div>

                    <div class="input-group hi-transfer-cost-calculator__money-pct">
                      <input class="form-control border-right-0 pr-0" 
                        type="number"
                        min={ 0 }
                        value={form.fields['transferFeePercentage'].value}
                        onChange={hopplerLinkState(this.form$, form, 'transferFeePercentage')}>
                      </input>
                      <div class="input-group-append">
                        <span class="input-group-text bg-white text-muted">%</span>
                      </div>
                    </div>
                  </div>

                </div>

                <div class="form-group">
                  <label>Notarial Fee</label>

                  <div class="hi-transfer-cost-calculator__money-row">
                    <div class="input-group hi-transfer-cost-calculator__money-php">
                      <div class="input-group-prepend">
                        <span class="input-group-text bg-white text-muted">Php</span>
                      </div>
                      {/* <input class="form-control border-left-0" 
                        type="number"
                        value={form.fields['notarialFee'].value}
                        onChange={hopplerLinkState(this.form$, form, 'notarialFee')}>
                      </input> */}
                      <CurrencyInput
                        extraClasses="border-left-0"
                        onValueChange={ this.onNotFeeChange }
                        value={ form.fields['notarialFee'].value }
                        fieldName="notarialFee"
                        placeholder={ form.fields['notarialFee'].value }
                        min={ 0 }>
                      </CurrencyInput>
                    </div>

                    <div class="input-group hi-transfer-cost-calculator__money-pct">
                      <input class="form-control border-right-0 pr-0" 
                        type="number"
                        min={ 0 }
                        value={form.fields['notarialFeePercentage'].value}
                        onChange={hopplerLinkState(this.form$, form, 'notarialFeePercentage')}>
                      </input>
                      <div class="input-group-append">
                        <span class="input-group-text bg-white text-muted">%</span>
                      </div>
                    </div>
                  </div>

                </div>

                <div class="form-group">
                  <label>Admin fee</label>

                  <div class="hi-transfer-cost-calculator__money-row">
                    <div class="input-group hi-transfer-cost-calculator__money-php">
                      <div class="input-group-prepend">
                        <span class="input-group-text bg-white text-muted">Php</span>
                      </div>
                      {/* <input class="form-control border-left-0" 
                        type="number"
                        value={form.fields['adminFee'].value}
                        onChange={hopplerLinkState(this.form$, form, 'adminFee')}>
                      </input> */}
                      <CurrencyInput
                        extraClasses="border-left-0"
                        onValueChange={ this.onAdminFeeChange }
                        value={ form.fields['adminFee'].value }
                        fieldName="adminFee"
                        placeholder={ form.fields['adminFee'].value }
                        min={ 0 }>
                      </CurrencyInput>
                    </div>

                    <div class="input-group hi-transfer-cost-calculator__money-pct">
                      <input class="form-control border-right-0 pr-0" 
                        type="number"
                        min={ 0 }
                        value={form.fields['adminFeePercentage'].value}
                        onChange={hopplerLinkState(this.form$, form, 'adminFeePercentage')}>
                      </input>
                      <div class="input-group-append">
                        <span class="input-group-text bg-white text-muted">%</span>
                      </div>
                    </div>
                  </div>
                </div>

              </form>
            </div>
            <div class="col-12 col-md-6">
              <div class="hi-transfer-cost-calculator__totals">
                <div class="hi-transfer-cost-calculator__totals-group">
                  <span class="hi-transfer-cost-calculator__totals-group-span">Property sale price</span>
                  <h3 class="hi-transfer-cost-calculator__totals-group__sale-price">
                    PHP { toDecimal(state.listingPrice) }
                  </h3>
                </div>
                <div class="hi-transfer-cost-calculator__totals-group">
                  <span class="hi-transfer-cost-calculator__totals-group-span">Total additional fees</span>
                  <h3 class="hi-transfer-cost-calculator__totals-group__total-additional-fees">
                    PHP { toDecimal(state.totalAdditionalFees) }
                  </h3>
                </div>

                <hr class="hi-transfer-cost-calculator__hr"/>

                <div class="hi-transfer-cost-calculator__totals-group">
                  <span class="hi-transfer-cost-calculator__totals-group-span">Total price</span>
                  <h3 class="hi-transfer-cost-calculator__totals-group__total-price">
                    PHP { toDecimal(state.totalPrice) }
                  </h3>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    );
  }
}